import { useApi } from 'api/api-context';
import { RevisionBulkActionProps } from 'components/tables/data/revision/types';
import { useMemo } from 'react';

import Button from 'components/Button';
import { IconCheck } from '@tabler/icons-react';
import useAcceptAction from 'components/tables/data/revision/actions/hooks/use-accept-action';
import { noop } from 'lodash';
import BulkConfirmMessage from 'components/tables/data/revision/actions/bulk/BulkConfirmMessage';
import { useConfirm } from 'components/modals/message/MessageProvider';
import { getBulkRevisionKindGroup } from 'components/tables/data/revision/actions/bulk/kind-utils';

/**
 * Performs a bulk accept on all selected rows.
 */
export default function BulkAcceptAction({
  revisions,
  onStatusChange = noop,
  onBulkActionComplete = noop,
}: RevisionBulkActionProps) {
  const { userId } = useApi();
  const { confirm } = useConfirm();
  const { acceptRevision } = useAcceptAction();
  const kindGroup = useMemo(() => getBulkRevisionKindGroup(revisions), [revisions]);

  const availableRevisions = useMemo(
    () =>
      kindGroup === 'mixed'
        ? []
        : revisions.filter(
            ({ revisionStatus: { slug }, technician }) => slug === 'created' && technician?.userId === userId
          ),
    [revisions, userId, kindGroup]
  );

  /**
   * Accepts all selected revisions.
   */
  const acceptAll = () =>
    confirm({
      title: availableRevisions[0]?.kind === 'revision' ? 'Prijať revízne správy' : 'Prijať formuláre',
      content: (
        <BulkConfirmMessage
          message={
            availableRevisions[0]?.kind === 'revision'
              ? 'Naozaj si prajete prijať vybrané revízne správy?'
              : 'Naozaj si prajete prijať vybrané formuláre?'
          }
          revisions={availableRevisions}
        />
      ),
      primaryButtonText: 'Prijať',
      secondaryButtonText: 'Zrušiť',
      onConfirm: async () => {
        for (const revision of availableRevisions) {
          await acceptRevision({ revision, onStatusChange });
        }
        onBulkActionComplete();
      },
    });

  if (availableRevisions.length === 0) {
    return <></>;
  }

  return (
    <Button variant="primary" size="sm" leftIcon={<IconCheck />} onClick={acceptAll}>
      Prijať ({availableRevisions.length})
    </Button>
  );
}
